import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import InternalInfoFoldersTag from '@components/framework/tags/InternalInfoFoldersTag.tsx';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  AuditEntityTypeEnum,
  DefaultVersionEnum,
  DetailSupplierNotificationOutDTO,
  EntityTypeEnum,
  GlobalTimeslots,
  PermissionTypesEnum,
  ShipmentStateCodeEnum,
  SnPartyConfigDTO,
  SNUpdateDataInfoCargoInDTO,
  SNUpdateDataLocationInDTO,
  SupplierNotificationLocationInfoOutDTO,
  SupplierNotificationTypeEnum,
  TagEnum,
  ExternalOrdersApiTypeEnum,
} from '@api/ringil-api.v2';
import { formatDistance, isNotBlank, isNullOrUndefined } from '@utils/utils';
import { formatPrice } from '@utils/priceUtils.ts';
import { CargoTypeCodeEnum } from '@utils/constants.ts';
import { isEntityOwner } from '@app/pages/customer/supplier-notification/utils.ts';
import { infoCargoUpdate, InternalInfoOverrides, internalInfoUpdate, locationStatusUpdate } from '@app/pages/customer/supplier-notification/supplierNotificationDetailMapping.ts';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import LocationDetailCard from '@components/framework/locationcard/LocationDetailCard.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import SvgCalendar from '@app/pages/dap/icons/Calendar.tsx';
import SvgAlert from '@icons/alert.svg?react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import LoadingReferenceBadge from '@components/ringil3/Badges/LoadingReferenceBadge.tsx';
import InputInternalNote from '@app/pages/customer/supplier-notification/InputInternalNote.tsx';
import { printDeliverySlots } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import { useCreateEditSupplierNotificationHeaderSidebar } from '@app/pages/customer/supplier-notification/sidebars/EditSupplierNotificationHeaderSidebar.tsx';
import { useCreateEditSupplierNotificationTimeslotSidebar } from '@app/pages/customer/supplier-notification/sidebars/EditSupplierNotificationTimeslotSidebar.tsx';
import { useCreateEditSupplierNotificationOrdersSidebar } from '@app/pages/customer/supplier-notification/sidebars/EditSupplierNotificationOrdersSidebar.tsx';
import Card from '@components/framework/card/Card.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilOrder from '@components/ringil3/RingilOrder/RingilOrder';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgLoadUp from '@icons/LoadUp.svg?react';
import { globalResponseToFrameworkOrder, makeSnOrderFieldSecured } from './snFrameworkOrders';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import SupplierNotificationMoreButtons from '@app/pages/customer/supplier-notification/SupplierNotificationMoreButtons.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import Badge from '@components/ringil3/Badges/Badge.tsx';
import CreatedBadge from '@components/ringil3/Badges/CreatedBadge.tsx';
import WeightBadge from '@components/ringil3/Badges/WeightBadge.tsx';
import PiecesBadge from '@components/ringil3/Badges/PiecesBadge.tsx';
import LinkedEntitiesBadge from '@components/ringil3/Badges/LinkedEntitiesBadge.tsx';
import ShipmentStatusTag from '@components/framework/tags/ShipmentStatusTag.tsx';
import TransportationTypeTag from '@components/framework/tags/TransportationTypeTag.tsx';
import CargoTypeTag from '@components/framework/tags/CargoTypeTag.tsx';
import CustomsRegimeTag from '@components/framework/tags/CustomsRegimeTag.tsx';
import ContainerInfoTag from '@components/framework/tags/ContainerInfoTag.tsx';
import RequirementsTag from '@components/framework/tags/RequirementsTag.tsx';
import CarrierNoteTag from '@components/framework/tags/CarrierNoteTag.tsx';
import InputTextTag from '@components/RingilComponents/tags/InputTextTag.tsx';
import SvgFile from '@icons/File.tsx';
import SvgNumber from '@icons/Number.tsx';
import { resolveSnTagConfig } from './snTagConfig';
import { SecuredConfig } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { POLICY_DESCRIPTION } from './snSecurityClauses';
import useNavigation from '@hooks/useNavigation';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import SvgTabInternalInfo from '@components/ringil3/icons/TabInternalInfo.tsx';
import SvgTabAttachments from '@components/ringil3/icons/TabAttachments.tsx';
import SvgTabMessages from '@components/ringil3/icons/TabMessages.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import ResponsiblePersonTag from '@components/framework/tags/ResponsiblePersonTag.tsx';
import InternalInfoNotifyTag from '@components/framework/tags/InternalInfoNotifyTag.tsx';
import InternalInfoGoodsInvoiceTag from '@components/framework/tags/InternalInfoGoodsInvoiceTag.tsx';
import IncotermCodeTag from '@components/framework/tags/IncotermCodeTag.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import EntityAuditLog, { EntityAuditLogHandle } from '@components/RingilComponents/audit/EntityAuditLog.tsx';
import SvgAuditlog from '@components/ringil3/icons/heading/Auditlog';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces';
import { useCreateAttachmentComponent } from '@components/ringil3/Features/Attachments/AttachementComponent.tsx';
import CustomerChatComponent from '@components/obsolete/Chat/CustomerChatComponent.tsx';
import CarrierChatComponent from '@app/pages/carrier/Chat/CarrierChatComponent.tsx';
import { useVisibilityAction } from '@hooks/useVisibilityAction/useVisibilityAction.tsx';
import SvgResponsible from '@icons/responsible.svg?react';
import { isSomeOpenTimeslotsExists } from '@utils/timeslotDateTime';
import SNCreateExpeditionAttachmentDialog from '@components/obsolete/DuplicateExpedition/SNCreateExpeditionAttachmentDialog.tsx';

interface ComponentProps {
  supplierNotificationId: string;
  /**
   * Shown inside another page (the SN list's split view, RD-2957): no breadcrumbs, and the heading
   * drops to h2 with the number linking to the full page. Everything else is the full detail.
   */
  embedded?: boolean;
  /** Called after the detail reloaded itself following a change — e.g. to refresh the list beside it. */
  onChanged?: () => void;
}

const renderAddress = (loc: SupplierNotificationLocationInfoOutDTO): ReactNode => (
  <>
    <div>{loc.streetNr}</div>
    <div>{`${loc.zipCode} ${loc.city}, ${loc.country}`}</div>
  </>
);

const hasSlots = (slots?: GlobalTimeslots) => (slots?.dayWithInterval?.length ?? 0) > 0;

// Redesigned SN detail (in progress): header + scroll-spy tabs. NOTIFICATION tab holds the location
// cards + Handling; INTERNAL INFO / ATTACHMENTS / MESSAGES are empty anchors, built in later steps.
const SupplierNotificationDetail: React.FC<ComponentProps> = ({ supplierNotificationId, embedded = false, onChanged }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const {
    detailSupplierNotificationById,
    getSNFeatureConfiguration,
    updateNotificationSupplierUpdateDataForLocationStatus,
    updateNotificationSupplierUpdateDataForInfoCargo,
    updateNotificationSupplierUpdateDataForInternalInfo,
    updateNotificationSupplierUpdateInternalNote,
    createExpeditionFromSN,
  } = useContext(ApiContext);
  const { loggedUser, hasPermission } = useContext(AuthenticatedUserContext);
  const [data, setData] = useState<DetailSupplierNotificationOutDTO>();
  const [snConfiguration, setSnConfiguration] = useState<SnPartyConfigDTO>();
  const [externalOrdersApiType, setExternalOrdersApiType] = useState<ExternalOrdersApiTypeEnum>();
  const [tagErrors, setTagErrors] = useState<Partial<Record<TagEnum, string>>>({});
  const [visibleSNAttachmentDialog, setVisibleSNAttachmentDialog] = useState(false);

  // RD-2738: per-tab SN permissions (READ = see, RW = edit) replace the former ROLE_SN_RW role check.
  const canEditSn = hasPermission([PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW]);
  const canEditInternalInfoPerm = hasPermission([PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_INTERNALINFO_RW]);
  const canEditAttachments = hasPermission([PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_ATTACHMENTS_RW]);
  const canEditChat = hasPermission([PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_CHAT_RW]);
  // Once a shipment (expedition) has been created from the SN it is locked for editing; only PERMISSION_SN_FIX re-opens it
  // (mirrors the legacy back-office "SN fix" capability). The permission itself is evaluated by useSecuredFlag.
  const snFix = useSecuredFlag('sn-detail-fix-after-shipment', {
    kind: 'editability',
    description: 'Edit a supplier notification after a shipment was created from it',
    permissions: [PermissionTypesEnum.PERMISSION_SN_FIX],
  });
  const shipmentCreated = data?.expeditionCreated != null;
  const editAfterShipmentOk = !shipmentCreated || snFix.granted;
  // Reusable editability condition: satisfied unless the SN is locked post-shipment without the fix permission.
  // A state of the notification, not "nothing to show": as `data` it would never reach the AI assistant.
  const afterShipmentGuard = {
    label: tr('SupplierNotificationDetail.editableAfterShipment', 'Editable after shipment created (PERMISSION_SN_FIX)'),
    satisfied: editAfterShipmentOk,
    description: 'Locked once a shipment has been created from the notification; a user with the SN fix permission (PERMISSION_SN_FIX) can still edit it',
    byDesign: false,
  };
  const disabled = !canEditSn || !editAfterShipmentOk;
  // RD-2496: labels, internal notes and attachments (add + copy-to-expedition) stay editable even after a shipment is
  // created — they are exempt from the SN_FIX post-shipment lock and only require the respective RW permission.
  const attachmentsDisabled = !canEditAttachments;
  const isOwner = loggedUser?.companyId === data?.entityOwnerCompanyId;
  // Audit collapse beta surface — resolves against the viewer's company (customer vs supplier are
  // different companies), so one surface differentiates both sides of this shared component.
  const snAuditCollapsed = useBetaVersion(BetaSurface.SUPPLIER_NOTIFICATION_AUDIT_LOG).version === DefaultVersionEnum.NEW;

  const auditLogRef = useRef<EntityAuditLogHandle>(null);
  // RD-2739: after any edit the collapsed audit panel resets to its "Show action history" state, on
  // both parties alike — bump this nonce in load() to key-remount the deferred SectionPanel. The legacy
  // (non-beta) path stays mounted, so it keeps refreshing in place via auditLogRef.reload().
  const [auditNonce, setAuditNonce] = useState(0);
  // The first load of a notification is not a change; every later one follows an edit (all edit paths
  // end in load()), which is when `onChanged` tells the page around an embedded detail.
  const loadedOnceRef = useRef<string | null>(null);
  const load = () =>
    detailSupplierNotificationById(supplierNotificationId, d => {
      setData(d);
      setAuditNonce(n => n + 1);
      auditLogRef.current?.reload();
      if (loadedOnceRef.current === supplierNotificationId) onChanged?.();
      loadedOnceRef.current = supplierNotificationId;
    });

  useEffect(() => {
    load();
  }, [supplierNotificationId]);

  useEffect(() => {
    if (data?.customerCompany != null) {
      getSNFeatureConfiguration(data.customerCompany.id, d => {
        // supplier/customer can't change after creation → force READONLY regardless of stored triple
        setSnConfiguration({ ...d.config, supplierAndCustomer: { ...d.config.supplierAndCustomer, editable: false } });
        setExternalOrdersApiType(d.common?.externalOrdersApiType);
      });
    }
  }, [data?.customerCompany]);

  const {
    EditSupplierNotificationHeaderSidebar,
    sidebarProps: headerSidebarProps,
    mountKey: headerSidebarKey,
    setVisible: setVisibleHeader,
  } = useCreateEditSupplierNotificationHeaderSidebar(() => load());
  // One control per leg: the two location cards each own their own panel, so a pickup edit can never
  // touch the delivery slots and vice versa. Same component, two independent open states.
  const {
    EditSupplierNotificationTimeslotSidebar,
    sidebarProps: loadingTimeslotSidebarProps,
    mountKey: loadingTimeslotSidebarKey,
    setVisible: setVisibleLoadingTimeslot,
  } = useCreateEditSupplierNotificationTimeslotSidebar(() => load());
  const {
    sidebarProps: unloadingTimeslotSidebarProps,
    mountKey: unloadingTimeslotSidebarKey,
    setVisible: setVisibleUnloadingTimeslot,
  } = useCreateEditSupplierNotificationTimeslotSidebar(() => load());
  const {
    EditSupplierNotificationOrdersSidebar,
    sidebarProps: ordersSidebarProps,
    mountKey: ordersSidebarKey,
    setVisible: setVisibleOrders,
  } = useCreateEditSupplierNotificationOrdersSidebar(() => load());
  const { internals: attachmentInternals, load: loadAttachments, AttachmentComponent, attachments } = useCreateAttachmentComponent(
    // Copy-to-expedition is an owner action (ported from the legacy detail); attachments are exempt from the SN_FIX lock.
    {
      enableCopyDocument: isOwner,
      disabled: attachmentsDisabled,
      invoicesRwPermissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_ATTACHMENTS_INVOICES_RW],
      enableDownloadZip: true,
      downloadZipApplicationId: data?.applicationId,
      // Attachment changes don't go through load(), so the page beside an embedded detail is told here.
      onChanged,
    },
    { entityType: EntityTypeEnum.SUPPLIER_NOTIFICATION, entityId: supplierNotificationId, disabled: attachmentsDisabled },
  );
  // Attachments + audit fetch lazily — only when their section first scrolls into view (scroll-spy mounts all sections).
  const attachmentsRef = useVisibilityAction<HTMLDivElement>(() => loadAttachments(EntityTypeEnum.SUPPLIER_NOTIFICATION, supplierNotificationId), 0, true);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [auditVisible, setAuditVisible] = useState(false);
  const auditRef = useVisibilityAction<HTMLDivElement>(() => setAuditVisible(true), 0, true);

  // Labels are per-company (each party drives its own set, not shared) → gated on SN write permission, not ownership; mirrors the create page. The ⋯ menu self-filters its own actions.
  // RD-2496: exempt from the post-shipment lock (no afterShipmentGuard) — labels stay editable without SN_FIX.
  const editLabels = useSecuredFlag('sn-detail-edit-labels', {
    kind: 'editability',
    description: 'Edit SN labels (per-company set)',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW],
  });
  const moreActions = useSecuredFlag('sn-detail-more-actions', {
    kind: 'visibility',
    description: 'SN more-actions menu (archive/duplicate/cancel/notify)',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW],
  });
  const showNotification = useSecuredFlag('sn-detail-show-notification', {
    kind: 'visibility',
    description: 'Notification tab',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_READ, PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW],
  });
  const showAttachments = useSecuredFlag('sn-detail-show-attachments', {
    kind: 'visibility',
    description: 'Attachments tab',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_ATTACHMENTS_READ, PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_ATTACHMENTS_RW],
  });
  const showChat = useSecuredFlag('sn-detail-show-chat', {
    kind: 'visibility',
    description: 'Messages tab',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_CHAT_READ, PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_CHAT_RW],
  });
  // Handling/orders pencil — honor legacy: gated on write permission, not ownership. No global orders-editable setting exists; per-field order rules (amount/value/note) apply inside the sidebar.
  const editOrders = useSecuredFlag('sn-detail-edit-handling', {
    kind: 'visibility',
    description: 'Edit handling / orders',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW],
    state: [afterShipmentGuard],
  });
  // Per-field order overlay — shared with create; generic key → SN policy FieldMode.
  const orderFieldSecured = makeSnOrderFieldSecured(
    snConfiguration,
    { type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW], satisfied: canEditSn },
    tr,
    'sn-detail-order',
  );

  // Scroll-spy tabs: all sections live on one scrolling view, the sticky strip highlights as you scroll (mirrors EditReservationOrders).
  const [activeTab, setActiveTab] = useState(0);
  // Internal-info & history tab — owner-only for now (legacy kept internal-info owner-gated); the non-owner view arrives with its Figma.
  // Tab is visible to both roles; the content differs (owner: editable tags + audit log; non-owner: "notified when created").
  const showInternalInfoTab = useSecuredFlag('sn-detail-show-internal-info', {
    kind: 'visibility',
    description: 'Internal info & history tab',
    permissions: [
      PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_INTERNALINFO_READ,
      PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_INTERNALINFO_RW,
    ],
  });
  const editInternalInfo = useSecuredFlag('sn-detail-edit-internal-info', {
    kind: 'editability',
    description: 'Owner only: only the company the notification is for edits its internal info',
    byDesign: true,
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_INTERNALINFO_RW],
    ownership: { label: 'Owner', satisfied: isOwner },
    state: [afterShipmentGuard],
  });
  // Ownership drives which internal-info view shows — expressed through the overlay (clause), not a raw isOwner ternary.
  const internalInfoOwnerView = useSecuredFlag('sn-detail-internal-info-owner', {
    kind: 'visibility',
    description: 'Owner view: only the company the notification is for sees and edits its internal info',
    byDesign: true,
    ownership: { label: 'Owner', satisfied: isOwner },
  });
  const internalInfoCounterpartyView = useSecuredFlag('sn-detail-internal-info-counterparty', {
    kind: 'visibility',
    description: 'Counterparty view: the other company sees only who was notified, in place of the owner view',
    byDesign: true,
    ownership: { label: 'Counterparty', satisfied: !isOwner },
  });
  // Change history is shown to any party holding the dedicated audit read permission (owner or
  // counterparty) — the BE endpoint row-filters the audit to the caller.
  const showAudit = useSecuredFlag('sn-detail-audit', {
    kind: 'visibility',
    description: 'SN change history / audit log',
    permissions: [PermissionTypesEnum.PERMISSION_SN_DETAIL_AUDITLOG_READ],
  });
  // RD-2496: internal note stays editable without SN_FIX (no afterShipmentGuard), unlike the rest of internal info.
  const editInternalNote = useSecuredFlag('sn-detail-edit-internal-note', {
    kind: 'editability',
    description: 'Owner only: only the company the notification is for edits its internal note',
    byDesign: true,
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_INTERNALINFO_RW],
    ownership: { label: 'Owner', satisfied: isOwner },
  });
  // RD-2496: attachments (add / copy-to-expedition) stay editable without SN_FIX (no afterShipmentGuard).
  const editAttachments = useSecuredFlag('sn-detail-edit-attachments', {
    kind: 'editability',
    description: 'Edit SN attachments',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_ATTACHMENTS_RW],
  });
  const editChat = useSecuredFlag('sn-detail-edit-chat', {
    kind: 'editability',
    description: 'Send SN messages',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_CHAT_RW],
  });
  // Create-expedition action — owner + SN write, gated by the SN's data state (no expedition/group-demand yet, open
  // timeslots on both legs, not awaiting confirmation). Mirrors the legacy detail's button visibility, expressed as clauses.
  const createExpeditionFlag = useSecuredFlag('sn-detail-create-expedition', {
    kind: 'visibility',
    description: 'Create expedition from supplier notification',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW],
    ownership: {
      label: 'Owner',
      satisfied: isEntityOwner(loggedUser, data?.entityOwnerCompanyId),
      description: 'Owner only: only the company the notification is for creates a shipment from it',
      byDesign: true,
    },
    // States of the notification, not "nothing to show": as `data` they would never reach the AI assistant.
    state: [
      { label: 'No expedition created yet', satisfied: isNullOrUndefined(data?.expeditionCreated), description: 'Offered only until a shipment has been created from the notification' },
      { label: 'No group demand created', satisfied: isNullOrUndefined(data?.groupDemandCreated), description: 'Not offered once a group demand has been created from the notification' },
      { label: 'Loading has timeslots', satisfied: (data?.supplierDeliverySlots?.dayWithInterval?.length ?? 0) > 0, description: 'Offered only when the notification has loading timeslots' },
      { label: 'Open loading timeslots', satisfied: isSomeOpenTimeslotsExists(data?.supplierDeliverySlots), description: 'Offered only while the notification has open loading timeslots' },
      { label: 'Open unloading timeslots', satisfied: isSomeOpenTimeslotsExists(data?.customerDeliverySlots), description: 'Offered only while the notification has open unloading timeslots' },
      { label: 'Not awaiting confirmation', satisfied: data?.shipmentStateCode !== ShipmentStateCodeEnum.WAITING, description: 'Not offered while the notification is waiting for confirmation' },
    ],
  });

  if (isNullOrUndefined(data)) return <></>;

  // Create-expedition affordances (ported from legacy detail). Both buttons share the same gate; the 1-click path skips the
  // FE create flow, the full path opens the copy-attachments dialog when attachments exist, else jumps straight to create.
  const expeditionDuplicationQueryParams = new URLSearchParams({
    routeAndParameters: 'true',
    cargoAndOrders: 'true',
    documents: 'false',
    labels: 'false',
    internalInfo: 'true',
    carrierSelection: 'true',
    carrierNote: 'true',
    linkToSN: 'true',
  });
  const createExpeditionFromSupplierNotification = (supplierNotificationId: string) =>
    createExpeditionFromSN(supplierNotificationId, expeditionCoordinates =>
      navigation.navigate(navigation.urlFunctions.createExpeditionDetailV2(expeditionCoordinates.expeditionId)),
    );

  // Per-tag config (visible/editable/required) — same resolver as the create page, fed by the detail DTO.
  const tagValueOf = (key: TagEnum): unknown => {
    switch (key) {
      case TagEnum.TRANSPORTATION_TYPE:
        return data.transportationTypeCode?.transportationType;
      case TagEnum.STATUS:
        return data.shipmentStateCode;
      case TagEnum.CARGO:
        return data.cargoType?.cargoType?.cargoTypeCode;
      case TagEnum.CUSTOMS_REGIME:
        return data.customsTypeCode;
      default:
        return undefined;
    }
  };
  const cfg = resolveSnTagConfig(snConfiguration, tagValueOf);
  const tagReadOnly = (key: TagEnum) => disabled || !cfg.isEditable(key);
  const tagSecured = (key: TagEnum): SecuredConfig => ({
    id: 'sn-detail-tag',
    instance: key,
    description: `Supplier notification field ${key}; the customer's notification settings decide whether it is shown and editable`,
    visible: [{ type: 'custom', label: tr('SupplierNotificationDetail.policyVisible', 'Visible by company policy'), satisfied: cfg.isVisible(key), description: POLICY_DESCRIPTION.visible, byDesign: false }],
    editable: [
      { type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW], satisfied: canEditSn },
      { type: 'custom', label: tr('SupplierNotificationDetail.policyEditable', 'Editable by company policy'), satisfied: cfg.isEditable(key), description: POLICY_DESCRIPTION.editable, byDesign: false },
      { type: 'state', ...afterShipmentGuard },
    ],
  });

  // Per-field validation mirrors the create page's chip rules; invalid edits are blocked with an inline error.
  const setTagError = (key: TagEnum, msg?: string) =>
    setTagErrors(prev => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  const requiredMsg = tr('SupplierNotificationDetail.fieldRequired', 'Required');
  const requiredText = (key: TagEnum, v?: string) => (cfg.isRequired(key) && !isNotBlank(v) ? requiredMsg : undefined);
  const validateCargo = (cargoTypeCode?: string, adrWeight?: number) => {
    if (cfg.isRequired(TagEnum.CARGO) && !cargoTypeCode) return requiredMsg;
    if (cargoTypeCode === CargoTypeCodeEnum.ADR_LQ && !(adrWeight > 0)) return tr('SupplierNotificationDetail.adrWeightRequired', 'ADR weight is required');
    return undefined;
  };
  const validateContainer = (code?: string, id?: string) => {
    if (cfg.isRequired(TagEnum.CONTAINER) && (!code || !id)) return tr('SupplierNotificationDetail.containerBoth', 'Enter container type and ID');
    if ((!!code && !id) || (!code && !!id)) return tr('SupplierNotificationDetail.containerBoth', 'Enter container type and ID');
    return undefined;
  };

  // Inline tag saves: send the full block + the single override, then reload (mirrors Expedition's editHeader pattern).
  const saveLocationStatus = (overrides: Partial<SNUpdateDataLocationInDTO>) =>
    updateNotificationSupplierUpdateDataForLocationStatus(data.id, locationStatusUpdate(data, overrides), () => load());

  const saveInfoCargo = (overrides: Partial<SNUpdateDataInfoCargoInDTO>) => {
    updateNotificationSupplierUpdateDataForInfoCargo(data.id, infoCargoUpdate(data, overrides), () => load());
  };

  // Internal info: one combined update (responsible person + notify + goods invoice + incoterm) + a separate note call. Send the full current state plus the single override, then reload (mirrors saveInfoCargo).
  const saveInternalInfo = (overrides: InternalInfoOverrides) =>
    updateNotificationSupplierUpdateDataForInternalInfo(data.id, internalInfoUpdate(data, overrides), () => load());
  const saveInternalNote = (note: string) => updateNotificationSupplierUpdateInternalNote(data.id, { internalNote: note }, () => load());

  const containerForm = data.containerInfo ? { containerInfoCode: data.containerInfo.containerInfoCode, containerId: data.containerInfo.containerId } : undefined;
  const saveContainer = (form: { containerInfoCode?: string; containerId?: string }) => {
    const code = form?.containerInfoCode || undefined;
    const id = form?.containerId || undefined;
    const e = validateContainer(code, id);
    setTagError(TagEnum.CONTAINER, e);
    if (!e) saveInfoCargo({ containerInfo: code || id ? { containerInfoCode: code, containerId: id } : undefined });
  };

  const pieces = data.orders.reduce((p, o) => p + (o.items?.reduce((q, i) => q + (i.amount ?? 0), 0) ?? 0), 0);
  const cargoTypeValue = data.cargoType ? { cargoTypeCode: data.cargoType.cargoType?.cargoTypeCode, adrWeight: data.cargoType.adrWeight } : undefined;

  const timeslotsVisible = snConfiguration?.timeslots?.visible !== false;
  const timeslotsEditable = !disabled && snConfiguration?.timeslots?.editable === true;
  // notify-recipient is shown to the counterparty (non-owner) only — the owner manages it on edit
  const showNotifyRecipient =
    snConfiguration?.requestNotifiedPersonInSn?.visible === true &&
    !isEntityOwner(loggedUser, data.customerCompany.id) &&
    (data.counterPartyNotifyPersons?.length ?? 0) > 0;

  const buildDateSection = (label: string, slots: GlobalTimeslots, testId: string, onEdit: () => void) =>
    timeslotsVisible
      ? {
          label,
          // With no timeslots the empty state already offers "Enter timeslots"; the pencil would open
          // the same panel, so it only appears once there is something to edit.
          editable: timeslotsEditable && hasSlots(slots),
          onEdit,
          content: hasSlots(slots) ? printDeliverySlots(slots, tr, testId) : undefined,
          emptyState: timeslotsEditable ? (
            <COInlineMessage
              variant={'alert'}
              icon={
                <span className={'text-content-tertiary'}>
                  <SvgAlert />
                </span>
              }
              buttons={
                <RingilButton
                  mode={'outlined-rectangle'}
                  size={'small'}
                  label={tr('SupplierNotificationDetail.enterTimeslots', 'Enter timeslots')}
                  onClick={onEdit}
                />
              }
              buttonsRight={true}
            >
              <span className={'text-content-primary'}>
                {tr('SupplierNotificationDetail.transportationDoesNotHaveAnyTimeslots', 'Transportation does not have any timeslots')}
              </span>
            </COInlineMessage>
          ) : undefined,
        }
      : undefined;

  // active index is derived as the array is built so it stays correct when a tab is hidden by permissions. Messages has no anchorId → it becomes a dialog in a later increment.
  const tabs: TabProps[] = [];
  if (showNotification.granted)
    tabs.push({
      label: tr('SupplierNotificationDetail.tabNotification', 'Notification'),
      active: activeTab === tabs.length,
      anchorId: 'sn-notification',
      icon: <SvgCalendar />,
    });
  if (showInternalInfoTab.granted)
    tabs.push({
      label: tr('RingilTabs.internalInfo', 'Internal info and history'),
      active: activeTab === tabs.length,
      anchorId: 'sn-internal-info',
      icon: <SvgTabInternalInfo />,
    });
  if (showAttachments.granted)
    tabs.push({ label: tr('RingilTabs.attachments', 'Attachments'), active: activeTab === tabs.length, anchorId: 'sn-attachments', icon: <SvgTabAttachments /> });
  // Messages has no anchor → its tab opens the chat dialog instead of scrolling/highlighting a section.
  let messagesTabIndex = -1;
  if (showChat.granted) {
    tabs.push({ label: tr('RingilTabs.messages', 'Messages'), active: activeTab === tabs.length, icon: <SvgTabMessages /> });
    messagesTabIndex = tabs.length - 1;
  }
  const handleTabChange = (index: number) => {
    if (index === messagesTabIndex) {
      setMessagesOpen(true);
      return;
    }
    setActiveTab(index);
  };

  return (
    <div className="flex flex-col gap-4">
      {!embedded && (
      <BreadCrumbComponent
        items={[
          {
            label: tr('SupplierNotificationDetail.supplierNotifications', 'Supplier notifications'),
            url: navigation.createNavigationLink(navigation.urlFunctions.createSupplierNotificationList()),
          },
          { label: data.applicationId },
        ]}
      />
      )}

      {/* TODO(SN rename): SN has no editable name yet — when added, gate the title pencil on an owner-only flag (isOwner && canEditSn). */}
      <Heading
        title={data.applicationId}
        // Embedded, the number is the way to the full page.
        titleLink={embedded ? navigation.createNavigationLink(navigation.urlFunctions.createSupplierNotificationDetail(data.id, SupplierNotificationTypeEnum.EFXX)) : undefined}
        headingVariant={embedded ? 'h2' : 'h1'}
        labels={
          <LabelsTag
            objectId={data.id}
            objectType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            resolveLabelsForType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            secured={{ id: 'sn-header-labels', label: 'LabelsTag', description: "The notification's labels", editable: editLabels }}
            dataTestId={'sn-header-labels'}
          />
        }
        buttons={[
          <SecuredArea
            key={'sn-create-expedition'}
            id={'sn-header-create-expedition'}
            label={'Create expedition'}
            description={'Creates a shipment from this notification; offered once, while it has open loading and unloading timeslots'}
            visible={createExpeditionFlag}>
            <div className={'flex flex-row gap-2 items-center'}>
              <RingilButton
                mode={'outlined-rectangle'}
                size={'big'}
                label={tr('SupplierNotificationDetail.createExpedition2', 'Create expedition 2')}
                onClick={() => {
                  // new way - without need to use create process in FE
                  createExpeditionFromSupplierNotification(data.id);
                }}
                dataTestId={'sn-createExpedition1Click'}
              />
              <RingilButton
                mode={'outlined-rectangle'}
                size={'big'}
                label={tr('SupplierNotificationDetail.createExpeditionNew', 'Create expedition NEW')}
                onClick={() => {
                  console.log(data)
                  if (attachments && attachments.length > 0) {
                    setVisibleSNAttachmentDialog(true);
                  } else {
                    navigation.navigate(
                      navigation.urlFunctions.createExpeditionFromEntity(data.id, EntityTypeEnum.SUPPLIER_NOTIFICATION, { queryParams: expeditionDuplicationQueryParams }),
                    );
                  }
                }}
                dataTestId={'sn-createExpeditionNew'}
              />
            </div>
          </SecuredArea>,
          <SecuredArea key={'sn-more'} id={'sn-header-more'} label={'SN more actions'} description={'Menu with more actions on the notification (duplicate, archive, cancel and others)'} visible={moreActions}>
            <SupplierNotificationMoreButtons
              archived={data.isArchived}
              supplierNotificationId={data.id}
              supplierNotificationApplicationId={data.applicationId}
              supplierLocationName={data.supplierLocation?.locationName ?? ''}
              isEntityOwner={isOwner}
              onChange={() => load()}
              isDAP={false}
              onCanceledSupplierNotification={() => load()}
            />
          </SecuredArea>,
        ]}
      />

      <Ringil3BadgeRow>
        {data.expeditionCreated && (
          <LinkedEntitiesBadge
            label={tr('SupplierNotificationDetail.transport', 'Transport')}
            links={[
              {
                id: data.expeditionCreated.expeditionId,
                applicationId: data.expeditionCreated.expeditionApplicationId,
                to: navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(data.expeditionCreated.expeditionId)),
              },
            ]}
            // A notification merged into a different shipment had its own one canceled to get there,
            // so the original reads as history — struck through, the way the merge sources do.
            strikeThrough={!isNullOrUndefined(data.mergedToExpedition) && data.mergedToExpedition.expeditionId !== data.expeditionCreated.expeditionId}
            dataTestId="sn-expeditionCreated"
          />
        )}
        {data.mergedToExpedition && data.mergedToExpedition.expeditionId !== data.expeditionCreated?.expeditionId && (
          <LinkedEntitiesBadge
            label={tr('SupplierNotificationDetail.mergedToExpedition', 'Merged into')}
            links={[
              {
                id: data.mergedToExpedition.expeditionId,
                applicationId: data.mergedToExpedition.expeditionApplicationId,
                to: navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(data.mergedToExpedition.expeditionId)),
              },
            ]}
            dataTestId="sn-mergedToExpedition"
          />
        )}
        <Badge label={tr('SupplierNotificationDetail.sender', 'Sender')} value={data.carrierCompany?.name} />
        <Badge label={tr('SupplierNotificationDetail.recipient', 'Recipient')} value={data.customerCompany?.name} />
        <Badge value={formatDistance(data.distance)} dataTestId="sn-distance" />
        {data.orderValue && <Badge value={formatPrice(data.orderValue.amount, data.orderValue.currencyCode)} dataTestId="sn-orderValue" />}
        <WeightBadge weight={data.totalWeight} />
        <PiecesBadge piecesCount={pieces} />
        <CreatedBadge createdAt={data.createdAt} createdBy={data.createdBy?.fullName} timezoneId={data.customerLocation?.timezoneId} />
      </Ringil3BadgeRow>

      <div className="flex flex-col gap-2">
        <Ringil3BadgeRow>
          <ShipmentStatusTag
            secured={tagSecured(TagEnum.STATUS)}
            code={data.shipmentStateCode}
            onChange={v => saveLocationStatus({ shipmentStateCode: v })}
            required={cfg.isRequired(TagEnum.STATUS)}
            readOnly={tagReadOnly(TagEnum.STATUS)}
            dataTestId={'sn-header-shipmentStatus'}
          />
          <TransportationTypeTag
            secured={tagSecured(TagEnum.TRANSPORTATION_TYPE)}
            transportationType={data.transportationTypeCode?.transportationType}
            onChange={v => saveLocationStatus({ transportationType: v })}
            required={cfg.isRequired(TagEnum.TRANSPORTATION_TYPE)}
            readOnly={tagReadOnly(TagEnum.TRANSPORTATION_TYPE)}
          />
          <CargoTypeTag
            secured={tagSecured(TagEnum.CARGO)}
            cargoType={cargoTypeValue}
            onChange={v => {
              const e = validateCargo(v?.cargoTypeCode, v?.adrWeight);
              setTagError(TagEnum.CARGO, e);
              if (!e) saveInfoCargo({ cargoType: { cargoTypeCode: v?.cargoTypeCode, adrWeight: v?.adrWeight } });
            }}
            required={cfg.isRequired(TagEnum.CARGO)}
            readOnly={tagReadOnly(TagEnum.CARGO)}
            errorMessage={tagErrors[TagEnum.CARGO]}
          />
          <CustomsRegimeTag
            secured={tagSecured(TagEnum.CUSTOMS_REGIME)}
            value={data.customsTypeCode}
            onChange={v => {
              const e = requiredText(TagEnum.CUSTOMS_REGIME, v);
              setTagError(TagEnum.CUSTOMS_REGIME, e);
              if (!e) saveInfoCargo({ customsTypeCode: v });
            }}
            required={cfg.isRequired(TagEnum.CUSTOMS_REGIME)}
            readOnly={tagReadOnly(TagEnum.CUSTOMS_REGIME)}
            errorMessage={tagErrors[TagEnum.CUSTOMS_REGIME]}
            dataTestId={'sn-cargoInfo-customsRegime'}
          />
          <InputTextTag
            secured={tagSecured(TagEnum.CMR_NUMBER)}
            label={tr('SupplierNotificationDetail.cmrNumber', 'CMR Number')}
            icon={<SvgFile />}
            value={data.cmrNumber}
            onChange={v => {
              const e = requiredText(TagEnum.CMR_NUMBER, v);
              setTagError(TagEnum.CMR_NUMBER, e);
              if (!e) saveInfoCargo({ cmrNumber: v });
            }}
            required={cfg.isRequired(TagEnum.CMR_NUMBER)}
            readOnly={tagReadOnly(TagEnum.CMR_NUMBER)}
            errorMessage={tagErrors[TagEnum.CMR_NUMBER]}
            dataTestId={'sn-cargoInfo-cmrId'}
          />
          <InputTextTag
            secured={tagSecured(TagEnum.BL_NUMBER)}
            label={tr('CreateSupplierNotification.blNumber', 'BL Number')}
            icon={<SvgFile />}
            value={data.blNumber}
            onChange={v => {
              const e = requiredText(TagEnum.BL_NUMBER, v);
              setTagError(TagEnum.BL_NUMBER, e);
              if (!e) saveInfoCargo({ blNumber: v });
            }}
            required={cfg.isRequired(TagEnum.BL_NUMBER)}
            readOnly={tagReadOnly(TagEnum.BL_NUMBER)}
            errorMessage={tagErrors[TagEnum.BL_NUMBER]}
            dataTestId={'sn-cargoInfo-blNumber'}
          />
          <InputTextTag
            secured={tagSecured(TagEnum.AWB_NUMBER)}
            label={tr('CreateSupplierNotification.awbNumber', 'AWB Number')}
            icon={<SvgFile />}
            value={data.awbNumber}
            onChange={v => {
              const e = requiredText(TagEnum.AWB_NUMBER, v);
              setTagError(TagEnum.AWB_NUMBER, e);
              if (!e) saveInfoCargo({ awbNumber: v });
            }}
            required={cfg.isRequired(TagEnum.AWB_NUMBER)}
            readOnly={tagReadOnly(TagEnum.AWB_NUMBER)}
            errorMessage={tagErrors[TagEnum.AWB_NUMBER]}
            dataTestId={'sn-cargoInfo-awbNumber'}
          />
          <ContainerInfoTag
            secured={tagSecured(TagEnum.CONTAINER)}
            field={'id'}
            value={containerForm}
            onChange={saveContainer}
            required={cfg.isRequired(TagEnum.CONTAINER)}
            readOnly={tagReadOnly(TagEnum.CONTAINER)}
            errorMessage={tagErrors[TagEnum.CONTAINER]}
            dataTestId={'sn-cargoInfo-containerId'}
          />
          <ContainerInfoTag
            secured={tagSecured(TagEnum.CONTAINER)}
            field={'type'}
            value={containerForm}
            onChange={saveContainer}
            required={cfg.isRequired(TagEnum.CONTAINER)}
            readOnly={tagReadOnly(TagEnum.CONTAINER)}
            errorMessage={tagErrors[TagEnum.CONTAINER]}
            dataTestId={'sn-cargoInfo-containerType'}
          />
          <InputTextTag
            secured={tagSecured(TagEnum.LOADING_REFERENCE)}
            label={tr('CreateShipment.referralCode', 'Loading reference')}
            icon={<SvgNumber />}
            value={data.referralCode}
            onChange={v => {
              const e = requiredText(TagEnum.LOADING_REFERENCE, v);
              setTagError(TagEnum.LOADING_REFERENCE, e);
              if (!e) saveInfoCargo({ referralCode: v });
            }}
            required={cfg.isRequired(TagEnum.LOADING_REFERENCE)}
            readOnly={tagReadOnly(TagEnum.LOADING_REFERENCE)}
            errorMessage={tagErrors[TagEnum.LOADING_REFERENCE]}
            dataTestId={'sn-cargoInfo-loadingReference'}
          />
          <RequirementsTag
            secured={tagSecured(TagEnum.CARRIER_REQUIREMENTS)}
            valueCodes={data.specialRequirements?.map(r => r.code)}
            valueNames={data.specialRequirements?.map(r => r.name)}
            onChange={v => saveInfoCargo({ specialRequirementIds: v })}
            required={cfg.isRequired(TagEnum.CARRIER_REQUIREMENTS)}
            readOnly={tagReadOnly(TagEnum.CARRIER_REQUIREMENTS)}
            companyId={data.customerCompany.id}
            dataTestId={'sn-cargoInfo-carrierRequirements'}
          />
        </Ringil3BadgeRow>

        <Ringil3BadgeRow>
          <CarrierNoteTag
            secured={tagSecured(TagEnum.CARRIER_NOTE)}
            value={data.note}
            fullWidth
            onChange={v => saveInfoCargo({ note: v })}
            required={cfg.isRequired(TagEnum.CARRIER_NOTE)}
            readOnly={tagReadOnly(TagEnum.CARRIER_NOTE)}
            dataTestId={'sn-cargoInfo-note'}
          />
        </Ringil3BadgeRow>
      </div>

      {/* navigation strip — not an edit affordance, so no security overlay */}
      <Tabs tabs={tabs} onTabChange={handleTabChange} />

      {showNotification.granted && (
      <div id={'sn-notification'}>
        <SectionPanel icon={<SvgCalendar />} heading={tr('SupplierNotificationDetail.theNotification', 'The notification')}>
          {/* left column = loading + unloading stacked; right column = Handling card */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div className="flex flex-col gap-4">
              <LocationDetailCard
                title={tr('LocationCard.loadingAddress', 'Loading address')}
                editable={!disabled}
                onEdit={() => setVisibleHeader(true)}
                companyName={data.carrierCompany?.name}
                address={renderAddress(data.supplierLocation)}
                timezoneId={data.supplierLocation?.timezoneId}
                referenceSlot={data.referralCode ? <LoadingReferenceBadge loadingReference={data.referralCode} /> : undefined}
                noteSlot={<InputInternalNote ownerCompanyId={data.customerCompany.id} partnerCompanyId={data.carrierCompany.id} readOnly={!canEditInternalInfoPerm} showTopDivider />}
                showDivider
                dateSection={buildDateSection(tr('LocationCard.pickupDate', 'Pickup date'), data.supplierDeliverySlots, 'sn-detail-loading-timeslot', () =>
                  setVisibleLoadingTimeslot(true),
                )}
                dataTestId={'sn-detail-loadingAddress'}
              />
              <LocationDetailCard
                title={tr('LocationCard.unloadingAddress', 'Unloading address')}
                editable={!disabled}
                onEdit={() => setVisibleHeader(true)}
                companyName={data.customerCompany?.name}
                address={renderAddress(data.customerLocation)}
                timezoneId={data.customerLocation?.timezoneId}
                contactsSlot={
                  showNotifyRecipient ? (
                    <div className="flex flex-col gap-1">
                      <div className="text-xs text-content-secondary">{tr('SupplierNotificationDetail.notifiedUsers', 'Notified users')}</div>
                      <div className="text-[15px] text-content-primary">{data.counterPartyNotifyPersons.map(u => u.name).join(', ')}</div>
                    </div>
                  ) : undefined
                }
                showDivider
                dateSection={buildDateSection(tr('LocationCard.deliveryDate', 'Delivery date'), data.customerDeliverySlots, 'sn-detail-unloading-timeslot', () =>
                  setVisibleUnloadingTimeslot(true),
                )}
                dataTestId={'sn-detail-unloadingAddress'}
              />
            </div>
            <Card>
              <div className="flex flex-col gap-4" data-testid={'sn-detail-handling'}>
                <div className="flex flex-row gap-2 items-center text-content-primary">
                  <SvgLoadUp className="rotate-90 w-4 h-4" />
                  <span className="text-base font-medium">{tr('SupplierNotificationDetail.handling', 'Handling')}</span>
                </div>
                <div className="flex flex-row items-center justify-between">
                  <COBadge type={'loading'} variant={'filled'}>
                    {tr('SupplierNotificationDetail.handlingLoadingBadge', 'Loading')}
                  </COBadge>
                  <SecuredArea id={'sn-handling-edit'} label={'Edit handling'} description={'Change the orders, handling units and goods of the notification'} visible={editOrders}>
                    <RingilButton mode={'table'} icon={<SvgPencil />} onClick={() => setVisibleOrders(true)} dataTestId={'sn-detail-handling-edit'} />
                  </SecuredArea>
                </div>
                <div className="flex flex-col gap-y-6">
                  {data.orders.map((order, i) => (
                    <RingilOrder key={i} type={'neutral'} order={order} dataTestIdPrefix={'sn-detail-handling'} orderIndex={i} />
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </SectionPanel>
      </div>
      )}

      {/* Internal info: owner vs counterparty view. History/audit below: gated on SN write permission. */}
      {showInternalInfoTab.granted && (
        <div id={'sn-internal-info'}>
          <SectionPanel icon={<SvgTabInternalInfo />} heading={tr('RingilTabs.internalInfo', 'Internal info and history')}>
            <SecuredArea
              id={'sn-internal-info-owner'}
              label={'Internal info (owner)'}
              description={"The notification owner's internal details and change history: responsible person, who to notify, goods invoice, delivery terms, internal note"}
              visible={internalInfoOwnerView}>
              <div className="flex flex-col gap-4">
                <Ringil3BadgeRow>
                  <ResponsiblePersonTag
                    secured={{ id: 'sn-ii-responsible-person', label: 'ResponsiblePersonTag', description: "Owner's person responsible for the notification", editable: editInternalInfo }}
                    icon={<SvgResponsible />}
                    person={data.notifyData?.responsiblePerson}
                    onChange={v => saveInternalInfo({ responsiblePerson: v })}
                  />
                  <SecuredArea id={'sn-ii-notify'} label={'InternalInfoNotifyTag'} description={"Owner's colleagues notified about the notification"} editable={editInternalInfo}>
                    <InternalInfoNotifyTag persons={data.notifyData?.usersToNotify} onChange={v => saveInternalInfo({ usersToNotify: v })} readonly={disabled} />
                  </SecuredArea>
                  <SecuredArea id={'sn-ii-goods-invoice'} label={'InternalInfoGoodsInvoiceTag'} description={"Owner's goods invoice number and due date"} editable={editInternalInfo}>
                    <InternalInfoGoodsInvoiceTag
                      value={{ goodsInvoiceNumber: data.internalInvoiceNumber, goodsInvoiceDate: data.invoiceDueDate }}
                      onChange={v => saveInternalInfo({ goodsInvoice: v })}
                      readonly={disabled}
                    />
                  </SecuredArea>
                  <IncotermCodeTag
                    secured={{ id: 'sn-ii-incoterm', label: 'IncotermCodeTag', description: 'Delivery terms (Incoterms) recorded by the owner', editable: editInternalInfo }}
                    code={data.deliveryConditionsIncotermCode}
                    onChange={v => saveInternalInfo({ incoterm: v })}
                    dataTestId={'sn-internalInfo-incoterm'}
                  />
                  <InternalInfoFoldersTag
                    secured={{ id: 'sn-ii-folders', label: 'InternalInfoFoldersTag', editable: editInternalInfo }}
                    folders={(data.folders ?? []).map(f => ({ id: f.folderId, name: f.folderName, active: true }))}
                    onChange={v => saveInternalInfo({ folders: v })}
                    entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
                  />
                  <InternalNoteTag
                    secured={{ id: 'sn-ii-internal-note', label: 'InternalNoteTag', description: "Owner's internal note; the other company never sees it", editable: editInternalNote }}
                    value={data.internalNote}
                    onChange={v => saveInternalNote(v)}
                  />
                </Ringil3BadgeRow>
              </div>
            </SecuredArea>
            <SecuredArea
              id={'sn-internal-info-counterparty'}
              label={'Notified when created (counterparty)'}
              description={"The other company's view of internal info: who was notified when the notification was created"}
              visible={internalInfoCounterpartyView}>
              <Ringil3BadgeRow>
                <ButtonTag
                  icon={<SvgResponsible />}
                  label={tr('SupplierNotificationDetail.notifiedWhenCreated', 'Notified when created')}
                  value={data.counterPartyNotifyPersons?.map(u => u.name).join(', ')}
                  readonly
                  dataTestId={'sn-internalInfo-notifiedWhenCreated'}
                />
              </Ringil3BadgeRow>
            </SecuredArea>
            <SecuredArea id={'sn-internal-info-audit'} label={'Change history'} description={'History of all changes made to the notification'} visible={showAudit}>
              {snAuditCollapsed ? (
                <SectionPanel
                  key={auditNonce}
                  icon={<SvgAuditlog />}
                  heading={tr(`AuditLog.heading`, `Audit log`)}
                  deferred={{
                    description: tr('AuditLog.revealHint', 'All actions performed by you or your colleagues.'),
                    revealLabel: tr('AuditLog.revealLabel', 'Show action history'),
                    dataTestId: 'snDetail-reveal-auditLog',
                  }}>
                  <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.SUPPLIER_NOTIFICATION} entityId={data.id} hideHeader />
                </SectionPanel>
              ) : (
                // ORIGINAL keeps the scroll-lazy mount (scroll-spy page must not fire the call on load).
                <div ref={auditRef}>{auditVisible && <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.SUPPLIER_NOTIFICATION} entityId={data.id} />}</div>
              )}
            </SecuredArea>
          </SectionPanel>
        </div>
      )}
      {/* Uniform section header via SectionPanel; AttachmentComponent's own heading is suppressed with hideHeader. */}
      {showAttachments.granted && (
        <div id={'sn-attachments'} ref={attachmentsRef}>
          <SectionPanel icon={<SvgTabAttachments />} heading={tr('RingilTabs.attachments', 'Attachments')}>
            <AttachmentComponent
              internals={attachmentInternals}
              entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
              entityId={data.id}
              disabled={attachmentsDisabled}
              hideHeader
              secured={{ id: 'sn-tab-attachments', label: 'AttachmentComponent', description: "The notification's documents and attachments", visible: showAttachments, editable: editAttachments }}
            />
          </SectionPanel>
        </div>
      )}

      <EditSupplierNotificationHeaderSidebar
        key={headerSidebarKey}
        {...headerSidebarProps}
        supplierNotificationId={data.id}
        initialData={{
          supplierId: data.carrierCompany.id,
          supplierLocationId: data.supplierLocation.locationId,
          supplierLocation: data.supplierLocation,
          transportationType: data.transportationTypeCode.transportationType,
          shipmentStateCode: data.shipmentStateCode,
          companyId: data.customerCompany.id,
          customerLocationId: data.customerLocation.locationId,
          customerLocation: data.customerLocation,
          customerDeliverySlots: data.customerDeliverySlots,
          supplierDeliverySlots: data.supplierDeliverySlots,
        }}
        configuration={snConfiguration}
      />
      <EditSupplierNotificationTimeslotSidebar
        key={`loading-${loadingTimeslotSidebarKey}`}
        {...loadingTimeslotSidebarProps}
        supplierNotificationId={data.id}
        leg={'loading'}
        slots={data.supplierDeliverySlots}
        otherSlots={data.customerDeliverySlots}
        locationId={data.supplierLocation?.locationId}
        locationTimezoneId={data.supplierLocation?.timezoneId}
      />
      <EditSupplierNotificationTimeslotSidebar
        key={`unloading-${unloadingTimeslotSidebarKey}`}
        {...unloadingTimeslotSidebarProps}
        supplierNotificationId={data.id}
        leg={'unloading'}
        slots={data.customerDeliverySlots}
        otherSlots={data.supplierDeliverySlots}
        locationId={data.customerLocation?.locationId}
        locationTimezoneId={data.customerLocation?.timezoneId}
      />
      <EditSupplierNotificationOrdersSidebar
        key={ordersSidebarKey}
        {...ordersSidebarProps}
        companyId={data.customerCompany.id}
        supplierNotificationId={data.id}
        supplierId={data.carrierCompany.id}
        customerLocationId={data.customerLocation.locationId}
        initialData={{ orders: data.orders.map(globalResponseToFrameworkOrder) }}
        configuration={snConfiguration}
        externalOrdersApiType={externalOrdersApiType}
        disabled={disabled}
        secured={{
          id: 'sn-detail-orders',
          label: tr('CreateSupplierNotification.orders', 'Orders'),
          description: 'The orders, handling units and goods the notification announces',
          editable: [{ type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_RW], satisfied: canEditSn }, { type: 'state', ...afterShipmentGuard }],
        }}
        fieldSecured={orderFieldSecured}
      />

      <SNCreateExpeditionAttachmentDialog visible={visibleSNAttachmentDialog} onCancel={() => setVisibleSNAttachmentDialog(false)} supplierNotificationId={data.id} />

      {/* Messages — chat dialog opened from the Messages tab; loads only on open. Owner→Customer, non-owner→Carrier (mirrors legacy). */}
      {showChat.granted &&
        (isOwner ? (
          <CustomerChatComponent
            companyId={loggedUser.companyId}
            entityId={data.id}
            entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            visible={messagesOpen}
            selectedCarrierId={data.carrierCompany.id}
            onHide={() => setMessagesOpen(false)}
            readOnly={!canEditChat}
            secured={{ id: 'sn-chat-dialog', label: 'CustomerChatComponent', description: 'Messages with the other company about the notification', visible: showChat, editable: editChat }}
          />
        ) : (
          <CarrierChatComponent
            companyId={data.customerCompany.id}
            entityId={data.id}
            entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            visible={messagesOpen}
            onHide={() => setMessagesOpen(false)}
            customerName={data.customerCompany.name}
            readOnly={!canEditChat}
            secured={{ id: 'sn-chat-dialog', label: 'CarrierChatComponent', description: 'Messages with the other company about the notification', visible: showChat, editable: editChat }}
          />
        ))}
    </div>
  );
};

export default SupplierNotificationDetail;
